import React from "react";

// Renderizador de Markdown para as respostas do assistente.
//
// Monta elementos React e nunca HTML: o texto vem do modelo, e parte dele vem
// do Diário. Um "<script>" ou "<img onerror>" dentro de uma intimação chega
// aqui como texto e continua texto — não há dangerouslySetInnerHTML em lugar
// nenhum, então não existe caminho para ele virar tag.
//
// Cobre o que os modelos de fato escrevem: títulos, parágrafos, negrito,
// itálico, código, listas (inclusive aninhadas), tabelas, citações, linhas e
// links. O resto sai como texto, o que é melhor que sair quebrado.

const LINK_SEGURO = /^(https?:|mailto:)/i;

// ─── Trechos dentro de uma linha ───────────────────────────────────────────

const PADROES = [
  { tipo: "codigo", regex: /`([^`]+)`/ },
  { tipo: "link", regex: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { tipo: "negrito", regex: /\*\*([^*]+?)\*\*|__([^_]+?)__/ },
  { tipo: "italico", regex: /\*([^*\s][^*]*?)\*|(?<![\w])_([^_\s][^_]*?)_(?![\w])/ },
];

export function inline(texto, chave = "i") {
  const saida = [];
  let resto = texto;
  let n = 0;
  while (resto) {
    let primeiro = null;
    for (const padrao of PADROES) {
      const achado = padrao.regex.exec(resto);
      if (achado && (!primeiro || achado.index < primeiro.achado.index)) {
        primeiro = { padrao, achado };
      }
    }
    if (!primeiro) {
      saida.push(resto);
      break;
    }
    const { padrao, achado } = primeiro;
    if (achado.index > 0) saida.push(resto.slice(0, achado.index));
    const k = `${chave}-${n++}`;
    if (padrao.tipo === "codigo") {
      saida.push(
        <code key={k} className="px-1 py-0.5 rounded bg-[#F4F7FB] border border-[#D1DEEE] text-[0.9em]">
          {achado[1]}
        </code>,
      );
    } else if (padrao.tipo === "link") {
      const destino = achado[2];
      saida.push(
        LINK_SEGURO.test(destino) ? (
          <a key={k} href={destino} target="_blank" rel="noopener noreferrer nofollow"
             className="text-[#003882] underline underline-offset-2">
            {inline(achado[1], k)}
          </a>
        ) : (
          // javascript:, data: e afins viram texto.
          <span key={k}>{achado[1]}</span>
        ),
      );
    } else if (padrao.tipo === "negrito") {
      saida.push(<strong key={k} className="font-semibold">{inline(achado[1] ?? achado[2], k)}</strong>);
    } else {
      saida.push(<em key={k}>{inline(achado[1] ?? achado[2], k)}</em>);
    }
    resto = resto.slice(achado.index + achado[0].length);
  }
  return saida;
}

// ─── Blocos ────────────────────────────────────────────────────────────────

const ITEM_LISTA = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const LINHA_TABELA = /^\s*\|.*\|\s*$/;
const SEPARADOR_TABELA = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function celulas(linha) {
  return linha.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

function montarLista(linhas, chave) {
  // Constrói a árvore pela indentação: item mais recuado vira sublista do anterior.
  const raiz = { filhos: [], ordenada: /\d/.test(ITEM_LISTA.exec(linhas[0])[2]) };
  const pilha = [{ recuo: -1, no: raiz }];
  for (const linha of linhas) {
    const [, espacos, marcador, conteudo] = ITEM_LISTA.exec(linha);
    const recuo = espacos.replace(/\t/g, "  ").length;
    while (pilha.length > 1 && recuo <= pilha[pilha.length - 1].recuo) pilha.pop();
    const pai = pilha[pilha.length - 1].no;
    const item = { texto: conteudo, filhos: [], ordenada: /\d/.test(marcador) };
    pai.filhos.push(item);
    pilha.push({ recuo, no: item });
  }
  const render = (no, k) => {
    const Tag = no.ordenada ? "ol" : "ul";
    return (
      <Tag key={k} className={`${no.ordenada ? "list-decimal" : "list-disc"} pl-6 space-y-1`}>
        {no.filhos.map((filho, i) => (
          <li key={`${k}-${i}`}>
            {inline(filho.texto, `${k}-${i}`)}
            {filho.filhos.length > 0 && render(filho, `${k}-${i}-s`)}
          </li>
        ))}
      </Tag>
    );
  };
  return render(raiz, chave);
}

export default function Markdown({ texto }) {
  const linhas = String(texto || "").replace(/\r\n/g, "\n").split("\n");
  const blocos = [];
  let i = 0;

  while (i < linhas.length) {
    const linha = linhas[i];
    const k = `b${blocos.length}`;

    if (!linha.trim()) { i += 1; continue; }

    // Bloco de código
    if (linha.trim().startsWith("```")) {
      const corpo = [];
      i += 1;
      while (i < linhas.length && !linhas[i].trim().startsWith("```")) { corpo.push(linhas[i]); i += 1; }
      i += 1;
      blocos.push(
        <pre key={k} className="bg-[#F4F7FB] border border-[#D1DEEE] rounded-md p-3 overflow-x-auto text-sm">
          <code>{corpo.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // Títulos: na conversa, até o h1 fica discreto — é uma resposta, não uma página.
    const titulo = /^(#{1,6})\s+(.*)$/.exec(linha);
    if (titulo) {
      const nivel = titulo[1].length;
      const classe = nivel <= 2 ? "text-lg font-semibold" : "text-base font-semibold";
      blocos.push(
        <p key={k} role="heading" aria-level={nivel} className={`${classe} text-[#001533]`}
           style={{ fontFamily: "Cabinet Grotesk" }}>
          {inline(titulo[2], k)}
        </p>,
      );
      i += 1;
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(linha)) {
      blocos.push(<hr key={k} className="border-[#D1DEEE]" />);
      i += 1;
      continue;
    }

    // Tabela: cabeçalho + separador + linhas
    if (LINHA_TABELA.test(linha) && i + 1 < linhas.length && SEPARADOR_TABELA.test(linhas[i + 1])) {
      const cabecalho = celulas(linha);
      i += 2;
      const corpo = [];
      while (i < linhas.length && LINHA_TABELA.test(linhas[i])) { corpo.push(celulas(linhas[i])); i += 1; }
      blocos.push(
        <div key={k} className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                {cabecalho.map((c, j) => (
                  <th key={j} className="text-left font-semibold border-b border-[#D1DEEE] px-2 py-1.5">
                    {inline(c, `${k}-h${j}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {corpo.map((linhaTabela, r) => (
                <tr key={r} className="border-b border-[#D1DEEE] last:border-0">
                  {cabecalho.map((_, j) => (
                    <td key={j} className="px-2 py-1.5 align-top">{inline(linhaTabela[j] || "", `${k}-${r}-${j}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (ITEM_LISTA.test(linha)) {
      const itens = [];
      while (i < linhas.length && ITEM_LISTA.test(linhas[i])) {
        let item = linhas[i];
        i += 1;
        // Linha de continuação recuada pertence ao item anterior.
        while (i < linhas.length && /^\s{2,}\S/.test(linhas[i]) && !ITEM_LISTA.test(linhas[i])) {
          item += ` ${linhas[i].trim()}`;
          i += 1;
        }
        itens.push(item);
      }
      blocos.push(montarLista(itens, k));
      continue;
    }

    if (linha.trimStart().startsWith(">")) {
      const citacao = [];
      while (i < linhas.length && linhas[i].trimStart().startsWith(">")) {
        citacao.push(linhas[i].trimStart().replace(/^>\s?/, ""));
        i += 1;
      }
      blocos.push(
        <blockquote key={k} className="border-l-2 border-[#D1DEEE] pl-3 text-[#335480]">
          {inline(citacao.join(" "), k)}
        </blockquote>,
      );
      continue;
    }

    // Parágrafo: junta linhas até uma em branco ou outro tipo de bloco.
    const paragrafo = [linha];
    i += 1;
    while (
      i < linhas.length && linhas[i].trim() &&
      !/^(#{1,6}\s|```|>)/.test(linhas[i].trimStart()) &&
      !ITEM_LISTA.test(linhas[i]) && !LINHA_TABELA.test(linhas[i])
    ) {
      paragrafo.push(linhas[i]);
      i += 1;
    }
    blocos.push(
      <p key={k}>
        {paragrafo.map((p, j) => (
          <React.Fragment key={j}>
            {j > 0 && <br />}
            {inline(p, `${k}-${j}`)}
          </React.Fragment>
        ))}
      </p>,
    );
  }

  return <div className="space-y-3 leading-relaxed" data-testid="markdown">{blocos}</div>;
}
